import { useUnlockPeoplePhoneNumber } from '@aibees/elephant/hooks/people';
import { usePhoneNumberEnrichmentDescription } from '@aibees/elephant/hooks/people/use-phone-number-enrichment-description';
import { ENRICHMENT_STATE } from '@aibees/elephant/types';
import { Icon } from '@aibees/shared/components';
import { classnames } from '@aibees/shared/utils';
import { Box, Card, CardContent, Stack, Typography } from '@mui/material';
import { memo } from 'react';
import { PhoneNumberInfo } from '../phone-number-info';
import { UnlockPersonPhoneNumber } from '../unlock-person-phone-number';
import * as Styled from './styles';
import * as SX from './sx';
import type { PersonPhoneNumberProps as Props } from './types';

export const PersonPhoneNumber = memo(
  ({ className, data, isPopoverVariant }: Props) => {
    const descriptionText = usePhoneNumberEnrichmentDescription(data);
    const { handleUnlock, unlocked, loading } =
      useUnlockPeoplePhoneNumber(data);

    const numbers = data.contact?.phone?.mobile;

    const noNumberFound =
      data.revelation?.phone_cached?.state === ENRICHMENT_STATE.FAILED;

    return (
      <Styled.Wrapper
        className={classnames('PersonPhoneNumber', className, {
          'phone-popover-variant': isPopoverVariant,
        })}
        sx={SX.root}
      >
        <Box>
          <Stack
            direction="row"
            gap={1}
            alignItems="center"
            justifyContent="space-between"
            mb={1}
          >
            <Typography variant="body3" fontWeight={600} color="grey.700">
              Mobile Phone Number
            </Typography>
            {data && !unlocked && (
              <UnlockPersonPhoneNumber
                item={data}
                handleUnlock={handleUnlock}
                loading={loading}
              />
            )}
          </Stack>
          <Typography
            variant="body3"
            color="grey.500"
            fontWeight={400}
            mb={0.5}
          >
            {descriptionText}
          </Typography>

          <Card variant="outlined">
            <CardContent padding="normal">
              <Stack gap={0.5}>
                {numbers &&
                  numbers.map((number) => (
                    <PhoneNumberInfo
                      key={number}
                      number={number}
                      isPrimary={number === numbers[0]}
                      isVerified
                    />
                  ))}
              </Stack>
              {noNumberFound && (
                <PhoneNumberInfo title="No mobile phone number found!" />
              )}
              {!unlocked && (
                <PhoneNumberInfo
                  title="**********************"
                  status="Unknown"
                />
              )}
            </CardContent>
          </Card>
          {noNumberFound && (
            <Box display="flex" gap={0.5} alignItems="center" mt={0.5}>
              <Icon name="info-circle-outline" size={14} color="grey.500" />
              <Typography variant="body4" fontWeight={400} color="grey.600">
                No valid number was found & no credits were deducted.
              </Typography>
            </Box>
          )}
        </Box>
      </Styled.Wrapper>
    );
  }
);

PersonPhoneNumber.displayName = 'PersonPhoneNumber';
